import { useState } from 'react';
import MonitoringConfigComponent from './MonitoringConfig';
import { MonitoringConfig } from './types';
import './App.css';

function App() {
  const [config, setConfig] = useState<MonitoringConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [error] = useState<string | null>(null);
  const [processingMessage, setProcessingMessage] = useState<string>('');


  const handleConfigChange = (newConfig: MonitoringConfig) => {
    setConfig(newConfig);
  };

  const handleSubmit = async () => {
    if (!config) {
      alert('Please complete the configuration form.');
      return;
    }

    if (!config.companyDescription || !config.email) {
      alert('Please fill in both company description and email fields.');
      return;
    }

    setLoading(true);
    setProcessingMessage('Processing your submission and analyzing relevant agencies...');
    try {
      console.log('Submitting monitoring configuration:', config);

      const response = await fetch(import.meta.env.DEV ? 'http://localhost:5001/submit' : 'https://legal-watchdog-api.vercel.app/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit configuration');
      }

      if (result.success) {
        setProcessingMessage('');
        alert(`Success! Your monitoring preferences have been saved. You will receive email notifications when new matching regulatory documents are published.`);
        setLastUpdate(new Date());
      } else {
        setProcessingMessage('');
        throw new Error(result.message || 'Configuration processing failed');
      }
    } catch (error) {
      console.error('Error submitting configuration:', error);
      setProcessingMessage('');
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to submit configuration. Please try again.'}`);
    } finally {
      setLoading(false);
      setProcessingMessage('');
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>🔍 Legal Watchdog</h1>
        <p>Monitor government agency updates and regulatory changes for your organization</p>
      </header>

      <main className="app-main">
        {error && (
          <div style={{ background: '#fee', border: '1px solid #fcc', padding: '1rem', borderRadius: '4px', marginBottom: '1rem' }}>
            <strong>Error:</strong> {error}
          </div>
        )}
        <MonitoringConfigComponent onConfigChange={handleConfigChange} />

        <div className="action-section">
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={loading || !config || !config.companyDescription || !config.email}
          >
            {loading ? 'Submitting...' : 'Start Monitoring'}
          </button>
          {processingMessage && (
            <div style={{ marginTop: '10px', color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
              {processingMessage}
            </div>
          )}
          {lastUpdate && (
            <div className="last-update">
              Configuration submitted: {lastUpdate.toLocaleString()}
            </div>
          )}
        </div>


        <div className="info-section">
          <h3>How it works</h3>
          <ul>
            <li><strong>Describe your business:</strong> Tell us what your company does and what regulatory information matters to you</li>
            <li><strong>Smart targeting:</strong> Our AI analyzes your description to determine which government agencies and document types are relevant to your business</li>
            <li><strong>Optional targeting:</strong> Specify particular agencies or keywords for more focused monitoring</li>
            <li><strong>Get personalized alerts:</strong> Receive email notifications when relevant regulatory changes are published that could impact your business</li>
            <li><strong>Stay ahead:</strong> Never miss important policy updates that could affect your operations or compliance requirements</li>
          </ul>

          <div className="note">
            <strong>What happens next:</strong> Our system uses AI to parse your company description and automatically checks for relevant regulatory documents from government agencies. You'll receive email alerts when new documents matching your business needs are published, complete with summaries and direct links.
          </div>
        </div>
      </main>

      <footer className="app-footer">
        <p>Legal Watchdog - Government Policy Monitoring System</p>
        <p>Data sourced from several government agencies and the <a href="https://www.federalregister.gov/" target="_blank" rel="noopener noreferrer">Federal Register</a></p>
      </footer>
    </div>
  );
}

export default App;